import { ChangeDetectionStrategy, Component, input, output, ViewEncapsulation } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { Avatar } from '../avatar/avatar';

/**
 * Simplified role info for display
 */
export interface UserRoleInfo {
  id: string;
}

/**
 * Data structure for UserListRow component
 */
export interface UserListRowData {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  directRoles: UserRoleInfo[];
  groupCount: number;
  hasLinkedPerson: boolean;
  banned?: boolean;
}

/**
 * UserListRow Component
 *
 * Displays a user's information in a horizontal row format for list views.
 * Shows user name, email, roles, and group count.
 *
 * @example
 * ```html
 * <zag-user-list-row
 *   [user]="userData"
 *   (rowClick)="onUserSelected($event)"
 *   (statusToggle)="onToggleStatus($event)">
 * </zag-user-list-row>
 * ```
 */
@Component({
  selector: 'zag-user-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TranslatePipe, ZagButton, ZagIcon, Avatar],
  host: {
    '[class.zag-user-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <zag-avatar class="zag-user-list-row__avatar" [name]="user().name" [src]="user().image ?? undefined" size="md" />

    <div class="zag-user-list-row__info">
      <div class="zag-user-list-row__name-wrapper">
        <span class="zag-user-list-row__name">{{ user().name }}</span>
        @if (user().banned) {
          <span class="zag-user-list-row__badge zag-user-list-row__badge--deactivated">{{
            'settings.users.status.deactivated' | translate
          }}</span>
        }
      </div>
      <span class="zag-user-list-row__email">{{ user().email }}</span>
    </div>

    <div class="zag-user-list-row__stats">
      <span class="zag-user-list-row__stat">
        <span zagIcon="security"></span>
        {{ user().directRoles.length }}
      </span>
      <span class="zag-user-list-row__stat">
        <span zagIcon="groups"></span>
        {{ user().groupCount }}
      </span>
      @if (!hideLinkStatus()) {
        @if (user().hasLinkedPerson) {
          <span class="zag-user-list-row__stat zag-user-list-row__stat--linked">
            <span zagIcon="link"></span>
          </span>
        } @else {
          <span class="zag-user-list-row__stat zag-user-list-row__stat--not-linked">
            <span zagIcon="link_off"></span>
          </span>
        }
      }
    </div>

    @if (canToggleStatus()) {
      <div class="zag-user-list-row__actions">
        <button
          zagButton="basic"
          zagType="secondary"
          zagSize="sm"
          type="button"
          class="zag-user-list-row__action"
          [attr.aria-label]="
            user().banned ? ('settings.users.actions.activate' | translate) : ('settings.users.actions.deactivate' | translate)
          "
          (click)="onToggleStatusClick($event)"
        >
          <span [zagIcon]="user().banned ? 'check_circle' : 'block'" aria-hidden="true"></span>
        </button>
      </div>
    }
  `,
  styles: [
    `
      .zag-user-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        min-width: 0;
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-user-list-row__avatar {
        flex-shrink: 0;
      }

      .zag-user-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-user-list-row__name-wrapper {
        display: flex;
        align-items: center;
        min-width: 0;
        flex-wrap: wrap;
        gap: var(--spacing-xs);
      }

      .zag-user-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-user-list-row__badge {
        display: inline-flex;
        padding: 2px 8px;
        border-radius: var(--border-radius-sm);
        font-size: var(--font-size-xs);
        font-weight: 500;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .zag-user-list-row__badge--deactivated {
        background-color: var(--color-warning-light);
        color: var(--color-warning);
      }

      .zag-user-list-row__email {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-user-list-row__stats {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        flex-shrink: 0;
      }

      .zag-user-list-row__stat {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);

        [zagIcon] {
          font-size: 16px;
        }
      }

      .zag-user-list-row__stat--linked {
        color: var(--color-success);
      }

      .zag-user-list-row__stat--not-linked {
        color: var(--color-error);
      }

      .zag-user-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-user-list-row__action.zag-button {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        color: var(--text-color-secondary);
        border: none;
        background: transparent;
        border-radius: var(--border-radius-sm);
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;

        &:hover:not(:disabled) {
          background-color: var(--surface-color-hover);
          color: var(--color-primary);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        &.zag-user-list-row__action--danger:hover:not(:disabled) {
          background-color: var(--color-error-bg, #fde8e8);
          color: var(--color-error);
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      @media (max-width: 768px) {
        .zag-user-list-row {
          flex-wrap: wrap;
          align-items: flex-start;
        }

        .zag-user-list-row__stats {
          display: none;
        }

        .zag-user-list-row__actions {
          margin-left: auto;
        }
      }

      @media (max-width: 640px) {
        .zag-user-list-row__info,
        .zag-user-list-row__actions {
          width: calc(100% - var(--entity-avatar-size-list) - var(--spacing-md));
        }

        .zag-user-list-row__email {
          overflow-wrap: anywhere;
          white-space: normal;
        }

        .zag-user-list-row__actions {
          justify-content: flex-end;
        }
      }
    `,
  ],
})
export class UserListRow {
  /** The user data to display */
  readonly user = input.required<UserListRowData>();

  /** Whether to hide the person link status */
  readonly hideLinkStatus = input(false);

  /** Whether the user status mutation control is available. */
  readonly canToggleStatus = input(true);

  /** Emitted when the row is clicked */
  readonly rowClick = output<UserListRowData>();

  /** Emitted when the status toggle button is clicked */
  readonly statusToggle = output<UserListRowData>();

  /** Handle row click - emits rowClick */
  onRowClick(): void {
    this.rowClick.emit(this.user());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle status toggle button click */
  onToggleStatusClick(event: Event): void {
    event.stopPropagation();
    this.statusToggle.emit(this.user());
  }
}
